import React from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { routePaths } from 'router';
import type { TCreateTripReq } from 'api/swagger';
import { useCreateTrip } from 'api/trips/use-create-trip';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard';
import { formatDate } from 'shared/utils/time/format-date';
import { FormDrawerWithButton } from 'components/common/form-drawer-with-button';
import { TripForm } from 'components/trips/trip-form';
import type { ITripFormFields } from 'components/trips/trip-form';

const TripCreateComponent: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutateAsync: createTrip, isLoading } = useCreateTrip();

  const handleFinish = async (values: ITripFormFields) => {
    const apiVariables: TCreateTripReq = {
      currency: values.currency,
      soldToPartners: values.soldToPartners,
      costPrice: values.costPrice,
      estimatedStartDate: values.estimatedDate[0] && formatDate(values.estimatedDate[0]),
      estimatedEndDate: values.estimatedDate[1] && formatDate(values.estimatedDate[1]),
      ...(values.soldToPartners
        ? {
            carrierId: values.carrier?.value,
            contactId: values.contact?.value,
          }
        : {
            truckId: values.truckId,
            trailerId: values.trailerId,
            trailerPlate: !values.trailerId ? values.trailerPlate : undefined,
          }),
    };

    const result = await createTrip(apiVariables);

    navigate(generatePath(routePaths.trip, { tripId: String(result.id) }));
  };

  return (
    <FormDrawerWithButton
      formComponent={TripForm}
      title={t('trip:add_trip')}
      buttonLabel={t('trip:add_trip')}
      isLoading={isLoading}
      onFinish={handleFinish}
    />
  );
};

export const TripCreate = withRoleGuard(TripCreateComponent, [
  UserRole.admin,
  UserRole.assistant,
  UserRole.transport_manager,
]);
